import EventEmitter from 'eventemitter3'

import { log } from '@/log'
import { Cart } from '@/models/cart/cart'
import RemoveItemFromCartService from '@/models/cart/service/remove-item-from-cart-service'
import { NoveltyService } from '@/services/novelty'
import { CartItemList } from '../cart-item'
import { CommandResult } from '../specification/deal-with-item-via-cart/index'

type EventName = 'start-clean-up' |
                 'finish-clean-up' |
                 'error-in-clean-up' |
                 'finish'

export default class CleanUpUnpublishedNoveltyItemObserver {
  static create(
    cart: Cart,
    noveltyService: NoveltyService,
    removeItemFromCart: RemoveItemFromCartService
  ): CleanUpUnpublishedNoveltyItemObserver {
    return new CleanUpUnpublishedNoveltyItemObserver(
      cart, noveltyService, removeItemFromCart
    )
  }

  private readonly eventEmitter = new EventEmitter()

  private constructor(
    private readonly cart: Cart,
    private readonly noveltyService: NoveltyService,
    private readonly removeItemFromCart: RemoveItemFromCartService
  ) {
    let promise: Promise<void> = Promise.resolve()

    this.noveltyService
      .onNoveltyNotFound(() =>
        // ノベルティ自体が非公開なら全削除.
        // 現時点(at 2019/10/22)では、ノベルティを複数同時に公開する想定は無い.
        // したがって、ノベルティ非公開 => ノベルティ商品の選択は全解除で良い.
        // @see https://github.com/my-color/front/issues/2648
        promise = this.cleanUp(
          this.cart.items
            // TODO: 現状「ノベルティのみ」の厳密な絞り込みはコンポーネント内部で定義されているが、model側に寄せたい
            .filter(item => item.isNovelty)
        )
      )
      .onFinish(async () => {
        await promise

        this.emit('finish')
      })
  }

  onStartCleanUp(listener: () => void): CleanUpUnpublishedNoveltyItemObserver {
    return this.on('start-clean-up', listener)
  }
  onFinishCleanUp(listener: (result: CommandResult) => void): CleanUpUnpublishedNoveltyItemObserver {
    return this.on('finish-clean-up', listener)
  }
  onErrorInCleanUp(listener: (result: CommandResult) => void): CleanUpUnpublishedNoveltyItemObserver {
    return this.on('error-in-clean-up', listener)
  }
  onFinish(listener: () => void): CleanUpUnpublishedNoveltyItemObserver {
    return this.on('finish', listener)
  }

  private async cleanUp(items: CartItemList) {
    if (items.length === 0) {
      return
    }

    this.emit('start-clean-up')

    return this.removeItemFromCart.removeSome(items.list, {
      items,
    }).then((result) => {
      if (!result.success) {
        this.emit('error-in-clean-up', result)
      }

      this.emit('finish-clean-up', result)
    })
  }

  private on(eventName: 'start-clean-up' | 'finish', listener: () => void): CleanUpUnpublishedNoveltyItemObserver
  private on(
    eventName: 'finish-clean-up' | 'error-in-clean-up',
    listener: (result: CommandResult) => void
  ): CleanUpUnpublishedNoveltyItemObserver
  private on(eventName: EventName, listener: (...args: any) => void): CleanUpUnpublishedNoveltyItemObserver {
    this.eventEmitter.on(eventName, listener)

    return this
  }
  private emit(eventName: 'start-clean-up' | 'finish'): void
  private emit(
    eventName: 'finish-clean-up' | 'error-in-clean-up',
    result: CommandResult
  ): void
  private emit(eventName: EventName, ...args: any): void {
    log.debug({ eventName })

    this.eventEmitter.emit(eventName, ...args)
  }
}
